import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { useAuth } from "@/contexts/AuthContext";
import {
  biometricsMarkedEnabled,
  biometricsSupported,
} from "@/lib/webauthn";

// Réplica de reference/expo-frontend/app/login.tsx.
export default function LoginPage() {
  const { login, loginWithBiometrics } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  // Mesma política do app original: o botão biométrico só aparece quando o
  // aparelho suporta E o usuário ativou a biometria neste aparelho.
  const [showBiometrics, setShowBiometrics] = useState(false);
  useEffect(() => {
    if (!biometricsMarkedEnabled()) return;
    void biometricsSupported().then(setShowBiometrics);
  }, []);

  const onBiometrics = async () => {
    setError(null);
    setBioLoading(true);
    const result = await loginWithBiometrics();
    setBioLoading(false);
    if (result.error) setError(result.error);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Preencha e-mail e senha.");
      return;
    }
    setError(null);
    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);
    if (result.error) {
      setError(result.error);
    }
  };

  return (
    <AuthLayout
      title="Entrar"
      subtitle="Use seu e-mail e senha para acessar o bolão."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4.5">
        <Field
          label="E-mail"
          tone="auth"
          type="email"
          autoComplete="email"
          placeholder="seu@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />
        <Field
          label="Senha"
          tone="auth"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
        />

        {error && (
          <p className="rounded-(--radius-field) bg-destructive/15 px-4 py-3 text-sm text-[#ffb4ad]">
            {error}
          </p>
        )}

        <Button type="submit" title="Entrar" loading={loading} className="mt-1" />

        {showBiometrics && (
          <Button
            type="button"
            variant="secondary"
            title="Entrar com biometria"
            icon={<Fingerprint size={16} />}
            loading={bioLoading}
            disabled={loading}
            onClick={() => void onBiometrics()}
          />
        )}
      </form>

      <div className="mt-6 flex items-center justify-center text-sm">
        <span className="text-surface-alt-foreground/55">
          Ainda não tem conta?{" "}
        </span>
        <Link to="/register" className="ml-1 font-bold text-accent">
          Cadastre-se
        </Link>
      </div>

      <div className="mt-3 flex justify-center">
        <Link
          to="/forgot-password"
          className="text-[13px] font-bold text-surface-alt-foreground/50"
        >
          Esqueci minha senha
        </Link>
      </div>
    </AuthLayout>
  );
}
